/**
 * This file contains serialized representations for JS-native constructs.
 * Currently this entails runtime functions like mutate and prefill, and validation functions from zod.
 */
import { z, type ZodType } from 'zod';
import { jsonPrimitiveSchema, type JsonPrimitive } from './json';

/**
 * Zod
 */
export type ZodDescriptor =
  | { zod: 'string' }
  | { zod: 'number' }
  | { zod: 'boolean' }
  | { zod: 'any' }
  | { zod: 'enum'; values: string[] }
  | { zod: 'literal'; value: JsonPrimitive }
  | { zod: 'array'; items: ZodDescriptor };

type StringDescriptor = Extract<ZodDescriptor, { zod: 'string' }>;
type NumberDescriptor = Extract<ZodDescriptor, { zod: 'number' }>;
type BooleanDescriptor = Extract<ZodDescriptor, { zod: 'boolean' }>;
type AnyDescriptor = Extract<ZodDescriptor, { zod: 'any' }>;
type EnumDescriptor = Extract<ZodDescriptor, { zod: 'enum' }>;
type LiteralDescriptor = Extract<ZodDescriptor, { zod: 'literal' }>;
type ArrayDescriptor = Extract<ZodDescriptor, { zod: 'array' }>;

const zodStringDescriptorSchema = z.object({
  zod: z.literal('string'),
}) as ZodType<StringDescriptor>;

const zodNumberDescriptorSchema = z.object({
  zod: z.literal('number'),
}) as ZodType<NumberDescriptor>;

const zodBooleanDescriptorSchema = z.object({
  zod: z.literal('boolean'),
}) as ZodType<BooleanDescriptor>;

const zodAnyDescriptorSchema = z.object({
  zod: z.literal('any'),
}) as ZodType<AnyDescriptor>;

const zodEnumDescriptorSchema = z.object({
  zod: z.literal('enum'),
  values: z.array(z.string()).min(1),
}) as ZodType<EnumDescriptor>;

const zodLiteralDescriptorSchema = z.object({
  zod: z.literal('literal'),
  value: jsonPrimitiveSchema,
}) as ZodType<LiteralDescriptor>;

export const zodDescriptorSchema: ZodType<ZodDescriptor> = z.lazy(() =>
  z.union([
    zodStringDescriptorSchema,
    zodNumberDescriptorSchema,
    zodBooleanDescriptorSchema,
    zodAnyDescriptorSchema,
    zodEnumDescriptorSchema,
    zodLiteralDescriptorSchema,
    z.object({
      zod: z.literal('array'),
      items: zodDescriptorSchema,
    }) as ZodType<ArrayDescriptor>,
  ]),
);

/**
 * JS function
 */
export const fnDescriptorSchema = z.object({
  body: z.string(),
});

export type FnDescriptor = z.infer<typeof fnDescriptorSchema>;
